<template>
  <div
    class="signup-progress"
    :aria-label="ariaLabel"
    role="progressbar"
    :aria-valuenow="current"
    :aria-valuemin="1"
    :aria-valuemax="total"
  >
    <div class="signup-progress__track">
      <div
        class="signup-progress__fill"
        :style="{ width: progressPercent + '%' }"
      ></div>
    </div>
    <div class="signup-progress__label">
      <span class="signup-progress__step">Step {{ current }}</span>
      <span class="signup-progress__sep" aria-hidden="true">·</span>
      <span class="signup-progress__name">{{ stepName || `of ${total}` }}</span>
    </div>
  </div>
</template>

<script>
/**
 * SignupProgressIndicator — single-fill progress bar for the wizard topbar.
 *
 * Replaced the earlier "5 pills + connector" hybrid: in a 160px-wide topbar
 * cluster, juggling done/active/upcoming pill states alongside a connecting
 * rail produced visual noise (hollow upcoming pills read as empty slots,
 * the rail looked uneven around different-width segments).
 *
 * The new pattern is a single continuous fill bar — the eyebrow underneath
 * (`STEP 4 · YOUR REWARDS`) carries the discrete step info, leaving the bar
 * free to do exactly one thing: communicate forward motion.
 *
 * Visual contract:
 *  - 4px tall, fully rounded track in lavender soft.
 *  - Fill is a brand-purple gradient ending at `current / total` of the
 *    track width, with a soft purple glow that bleeds beyond the leading
 *    edge so it reads as live progress, not a static segment.
 *
 * Props:
 *  - current:  Number   1..total
 *  - total:    Number   default 5
 *  - stepName: String   active step's human-readable name (e.g. "Your team")
 */
export default {
  name: 'SignupProgressIndicator',
  props: {
    current:  { type: Number, required: true },
    total:    { type: Number, default: 5 },
    stepName: { type: String, default: '' },
  },
  computed: {
    progressPercent() {
      const t = Math.max(1, this.total)
      const c = Math.max(1, Math.min(this.current, t))
      return Math.round((c / t) * 100)
    },
    ariaLabel() {
      const tail = this.stepName ? ` — ${this.stepName}` : ''
      return `Signup progress, step ${this.current} of ${this.total}${tail}`
    },
  },
}
</script>

<style lang="scss" scoped>
@import './_shared.scss';

.signup-progress {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  font-family: 'Lato', system-ui, sans-serif;
}

.signup-progress__track {
  position: relative;
  width: 160px;
  height: 4px;
  border-radius: 999px;
  background: rgba(108, 48, 204, 0.12);
  overflow: hidden;
}

.signup-progress__fill {
  position: absolute;
  inset: 0 auto 0 0;
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(90deg,
    var(--signup-brand-deeper) 0%,
    var(--signup-brand) 100%);
  /* Subtle outer glow on the fill bleeds slightly past the track —
     reads as live progress, not a static block. */
  box-shadow: 0 0 8px rgba(108, 48, 204, 0.32);
  transition: width 360ms cubic-bezier(.22, 1, .36, 1);
}

.signup-progress__label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--signup-text-muted);
}
.signup-progress__step {
  color: var(--signup-brand);
}
.signup-progress__sep {
  color: var(--signup-border-soft);
  letter-spacing: 0;
  font-weight: 600;
}
.signup-progress__name {
  color: var(--signup-text);
}

@media (max-width: 560px) {
  .signup-progress__track { width: 120px; height: 3px; }
  .signup-progress__label { font-size: 9.5px; letter-spacing: 0.14em; }
}
</style>
